import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';
import UserMenu from './UserMenu';
import { getAstroInfo, formatDateRu } from '../utils/astroUtils';
import logo from '../assets/logo.png';

const Header: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('register');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const burgerButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Определяем мобильное устройство
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Keyboard navigation: Escape закрывает мобильное меню
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        burgerButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  // Астрологическая информация
  const astroInfo = getAstroInfo();
  const currentDate = formatDateRu();

  const openRegister = () => { setAuthMode('register'); setShowAuthModal(true); };
  const openLogin = () => { setAuthMode('login'); setShowAuthModal(true); };
  const handleLoginSuccess = () => { navigate('/dashboard'); };

  const navItems = isAuthenticated
    ? [
        { label: 'Моя страница', path: '/dashboard', public: true },
        { label: 'Натальная карта', path: '/natal', public: true },
        { label: 'Нумерология', path: '/numerology', public: true },
        { label: 'Совместимость', path: '/compatibility', public: true },
        { label: 'Прогнозы', path: '/forecasts', public: true },
        { label: 'Статьи', path: '/articles', public: true },
      ]
    : [
        { label: 'Главная', path: '/', public: true },
        { label: 'Натальная карта', path: '/natal', public: false },
        { label: 'Нумерология', path: '/numerology', public: false },
        { label: 'Совместимость', path: '/compatibility', public: false },
        { label: 'Прогнозы', path: '/forecasts', public: false },
        { label: 'Статьи', path: '/articles', public: true },
      ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // TODO: реализовать поиск — пока redirect на /articles?q=...
      navigate(`/articles?q=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <>
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 10000,
        background: 'rgba(10, 8, 20, 0.85)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: isMobile ? '0 12px' : '0 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '70px',
        }}>
          {/* Logo + Astro Info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Logo */}
            <div
              className="logo-container"
              style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
            >
              <img
                src={logo}
                alt="Astranum"
                style={{
                  width: isMobile ? '40px' : '50px',
                  height: isMobile ? '40px' : '50px',
                  objectFit: 'contain',
                  borderRadius: '50%',
                  border: '2px solid #d4af37',
                }}
              />
              <span
                className="logo-text"
                style={{
                  fontSize: isMobile ? '20px' : '24px',
                  fontWeight: '500',
                  color: '#d4af37',
                  fontFamily: 'serif',
                  letterSpacing: '2px',
                }}
              >
                Astranum
              </span>
            </div>

            {/* Astro Info (left side) — знак Солнца */}
            <div
              className="astro-info"
              style={{
                display: isMobile ? 'none' : 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '13px',
                fontFamily: 'var(--font-sans)',
                color: '#d4af37',
              }}
            >
              <span>{astroInfo.sunSignIcon} {astroInfo.sunSign}</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
          }} className="desktop-nav">
            {navItems.map((item) => {
              const isDisabled = !item.public && !isAuthenticated;
              const isSpecial = item.special;

              return (
                <button
                  key={item.path}
                  onClick={() => {
                    if (!isDisabled) {
                      navigate(item.path);
                    }
                  }}
                  disabled={isDisabled}
                  aria-label={item.label + (isDisabled ? ' (требуется авторизация)' : '')}
                  style={{
                    background: isSpecial && !isAuthenticated ? 'rgba(212, 175, 55, 0.1)' : 'none',
                    border: isSpecial ? '1px solid rgba(212, 175, 55, 0.4)' : 'none',
                    color: isDisabled ? '#555' : isSpecial ? '#d4af37' : '#b0b0b0',
                    fontSize: '14px',
                    padding: '8px 16px',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    borderRadius: '8px',
                    transition: 'all 0.3s',
                    fontFamily: 'var(--font-sans)',
                    letterSpacing: '0.5px',
                    opacity: isDisabled ? 0.5 : 1,
                    touchAction: 'manipulation',
                    minHeight: '44px',
                  }}
                  onMouseEnter={(e) => {
                    if (!isDisabled) {
                      e.currentTarget.style.color = '#d4af37';
                      e.currentTarget.style.background = isSpecial ? 'rgba(212, 175, 55, 0.15)' : 'rgba(212, 175, 55, 0.08)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = isDisabled ? '#555' : isSpecial ? '#d4af37' : '#b0b0b0';
                    e.currentTarget.style.background = isSpecial && !isAuthenticated ? 'rgba(212, 175, 55, 0.1)' : 'none';
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Right Section */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            {/* Search Button */}
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              aria-label="Поиск"
              style={{
                background: 'none',
                border: 'none',
                color: '#d4af37',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                transition: 'all 0.3s',
                touchAction: 'manipulation',
                minWidth: '44px',
                minHeight: '44px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
              }}
            >
              🔍
            </button>

            {/* Auth Buttons / User Menu */}
            {isLoading ? (
              <div style={{ color: '#888', fontSize: '12px' }}>Загрузка...</div>
            ) : isAuthenticated ? (
              <>
                <UserMenu onNavigate={(path) => navigate(path)} />
              </>
            ) : (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }} className="auth-buttons">
                <button
                  onClick={openLogin}
                  aria-label="Войти"
                  style={{
                    background: 'none',
                    border: '1px solid rgba(212, 175, 55, 0.4)',
                    color: '#d4af37',
                    fontSize: '14px',
                    padding: '8px 20px',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    fontFamily: 'var(--font-sans)',
                    touchAction: 'manipulation',
                    minHeight: '44px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)';
                    e.currentTarget.style.borderColor = '#d4af37';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                    e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.4)';
                  }}
                >
                  Войти
                </button>
                <button
                  onClick={openRegister}
                  aria-label="Регистрация"
                  style={{
                    background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
                    border: 'none',
                    padding: '8px 24px',
                    borderRadius: '20px',
                    color: '#0a0a1a',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)',
                    transition: 'all 0.3s',
                    fontFamily: 'var(--font-sans)',
                    touchAction: 'manipulation',
                    minHeight: '44px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(212, 175, 55, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.3)';
                  }}
                >
                  Регистрация
                </button>
              </div>
            )}

            {/* Mobile Menu Button — только на мобильных */}
            <button
              ref={burgerButtonRef}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMobileMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
              style={{
                display: isMobile ? 'block' : 'none',
                background: 'none',
                border: 'none',
                color: '#d4af37',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '8px',
                touchAction: 'manipulation',
                minWidth: '44px',
                minHeight: '44px',
              }}
              className="mobile-menu-btn"
            >
              {isMobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {isSearchOpen && (
          <div style={{
            padding: '16px 20px',
            background: 'rgba(10, 8, 20, 0.95)',
            borderTop: '1px solid rgba(212, 175, 55, 0.1)',
            animation: 'fadeIn 0.3s ease',
          }}>
            <form onSubmit={handleSearch} style={{
              maxWidth: '600px',
              margin: '0 auto',
              display: 'flex',
              gap: '12px',
            }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Поиск по сайту..."
                style={{
                  flex: 1,
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: '12px',
                  padding: '12px 20px',
                  color: '#fff',
                  fontSize: '16px',
                  outline: 'none',
                }}
                autoFocus
              />
              <button
                type="submit"
                style={{
                  background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 24px',
                  color: '#0a0a1a',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Найти
              </button>
            </form>
          </div>
        )}

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            id="mobile-menu"
            role="navigation"
            aria-label="Мобильная навигация"
            style={{
              padding: '20px',
              background: 'rgba(10, 8, 20, 0.98)',
              borderTop: '1px solid rgba(212, 175, 55, 0.1)',
              animation: 'fadeIn 0.3s ease',
              maxHeight: 'calc(100vh - 70px)',
              overflowY: 'auto',
            }}
            className="mobile-menu"
          >
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Секция 1: Поиск */}
              <div style={{ marginBottom: '16px' }}>
                <form onSubmit={handleSearch} role="search" style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Поиск по сайту..."
                    aria-label="Поиск по сайту"
                    style={{
                      flex: 1,
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      borderRadius: '12px',
                      padding: '12px 20px',
                      color: '#fff',
                      fontSize: '16px',
                      outline: 'none',
                      touchAction: 'manipulation',
                    }}
                    autoFocus
                  />
                  <button
                    type="submit"
                    aria-label="Найти"
                    style={{
                      background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
                      border: 'none',
                      borderRadius: '12px',
                      padding: '12px 24px',
                      color: '#0a0a1a',
                      fontWeight: '600',
                      cursor: 'pointer',
                      touchAction: 'manipulation',
                      minWidth: '44px',
                      minHeight: '44px',
                    }}
                  >
                    Найти
                  </button>
                </form>
              </div>

              {/* Разделитель */}
              <div style={{
                height: '1px',
                background: 'rgba(212, 175, 55, 0.1)',
                margin: '8px 0',
              }} />

              {/* Секция 2: Навигация */}
              {navItems.map((item) => {
                const isDisabled = !item.public && !isAuthenticated;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      if (!isDisabled) {
                        navigate(item.path);
                        setIsMobileMenuOpen(false);
                      }
                    }}
                    disabled={isDisabled}
                    aria-label={item.label + (isDisabled ? ' (требуется авторизация)' : '')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: isDisabled ? '#555' : '#b0b0b0',
                      fontSize: '16px',
                      padding: '12px 16px',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      borderRadius: '8px',
                      textAlign: 'left',
                      transition: 'all 0.3s',
                      touchAction: 'manipulation',
                      minHeight: '44px',
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  >
                    {item.label}
                  </button>
                );
              })}

              {/* Разделитель */}
              <div style={{
                height: '1px',
                background: 'rgba(212, 175, 55, 0.1)',
                margin: '8px 0',
              }} />

              {/* Секция 3: Авторизация / UserMenu */}
              {isLoading ? null : isAuthenticated ? (
                <div style={{ padding: '8px 0' }}>
                  <UserMenu onNavigate={(path) => { navigate(path); setIsMobileMenuOpen(false); }} />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px 0' }}>
                  <button
                    onClick={() => { openLogin(); setIsMobileMenuOpen(false); }}
                    aria-label="Войти"
                    style={{
                      background: 'none',
                      border: '1px solid rgba(212, 175, 55, 0.4)',
                      color: '#d4af37',
                      fontSize: '16px',
                      padding: '12px 20px',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      touchAction: 'manipulation',
                      minHeight: '44px',
                    }}
                  >
                    Войти
                  </button>
                  <button
                    onClick={() => { openRegister(); setIsMobileMenuOpen(false); }}
                    aria-label="Регистрация"
                    style={{
                      background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '20px',
                      color: '#0a0a1a',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      touchAction: 'manipulation',
                      minHeight: '44px',
                    }}
                  >
                    Регистрация
                  </button>
                </div>
              )}
            </nav>
          </div>
        )}
      </header>

      {showAuthModal && (
        <AuthModal
          mode={authMode}
          onClose={() => setShowAuthModal(false)}
          onSwitchMode={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
          onLoginSuccess={handleLoginSuccess}
        />
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav {
            display: none !important;
          }
          .auth-buttons {
            display: none !important;
          }
          /* Скрываем Astro Info на мобильных чтобы всё помещалось */
          .astro-info {
            display: none !important;
          }
          /* Уменьшаем логотип на мобильных */
          .logo-container img {
            width: 40px !important;
            height: 40px !important;
          }
          .logo-text {
            font-size: 20px !important;
          }
          /* UserMenu dropdown на мобильных */
          .user-menu-dropdown {
            position: fixed !important;
            top: 70px !important;
            right: 12px !important;
            left: auto !important;
            max-width: calc(100vw - 24px) !important;
          }
        }
      `}</style>
    </>
  );
};

export default Header;
